import { useEffect, useRef, useState } from 'react';
import {
 Box,
 Button,
 Flex,
 FormControl,
 FormLabel,
 IconButton,
 Input,
 Link,
 Modal,
 ModalBody,
 ModalCloseButton,
 ModalContent,
 ModalFooter,
 ModalHeader,
 ModalOverlay,
 Select,
 Stack,
 Text,
 Textarea,
 useToast,
} from '@chakra-ui/react';
import { FiDownload, FiX } from 'react-icons/fi';
import {
 createActivity,
 updateActivity,
 getResponsibleCandidates,
 addAttachment,
 deleteAttachment,
} from '../../services/scheduleService';
import { AttentionModal } from '../../components/Modals';
import { ACTIVITY_STATUS_OPTIONS, formatDate } from './constants';

const EMPTY = {
 name: '',
 description: '',
 fase: '',
 start_date: '',
 end_date: '',
 status: 'NAO_INICIADO',
 id_responsible: '',
 executor_observations: '',
};

export default function ActivityModal({
 isOpen,
 onClose,
 idSchedule,
 idCompany,
 activity,
 presetStatus,
 existingPhases = [],
 onSaved,
}) {
 const toast = useToast();
 const isEdit = !!activity;
 const fileInput = useRef(null);

 const [form, setForm] = useState(EMPTY);
 const [responsibles, setResponsibles] = useState([]);
 const [attachments, setAttachments] = useState([]);
 // Anexos escolhidos durante a CRIAÇÃO ficam pendentes (File) e só sobem depois
 // que a atividade é criada — antes disso não há id_activity para o upload.
 const [pendingFiles, setPendingFiles] = useState([]);
 const [attachmentToRemove, setAttachmentToRemove] = useState(null);
 const [saving, setSaving] = useState(false);
 const [uploading, setUploading] = useState(false);

 useEffect(() => {
  if (!isOpen) return;
  if (idCompany) {
   getResponsibleCandidates(idCompany)
    .then((data) => setResponsibles(Array.isArray(data) ? data : []))
    .catch(() => setResponsibles([]));
  }
 }, [isOpen, idCompany]);

 useEffect(() => {
  if (!isOpen) return;
  if (isEdit) {
   setForm({
    name: activity.name || '',
    description: activity.description || '',
    fase: activity.fase || '',
    start_date: (activity.start_date || '').slice(0, 10),
    end_date: (activity.end_date || '').slice(0, 10),
    status: activity.status || 'NAO_INICIADO',
    id_responsible: activity.id_responsible || '',
    executor_observations: activity.executor_observations || '',
   });
   setAttachments(activity.attachments || []);
  } else {
   setForm({ ...EMPTY, status: presetStatus || 'NAO_INICIADO' });
   setAttachments([]);
  }
  setPendingFiles([]);
 }, [isOpen, activity, isEdit, presetStatus]);

 function setField(field, value) {
  setForm((f) => ({ ...f, [field]: value }));
 }

 function buildPayload() {
  return {
   name: form.name.trim(),
   description: form.description || null,
   fase: form.fase?.trim() || null,
   start_date: form.start_date,
   end_date: form.end_date,
   status: form.status,
   id_responsible: form.id_responsible ? Number(form.id_responsible) : null,
   executor_observations: form.executor_observations || null,
  };
 }

 async function handleSubmit() {
  if (!form.name.trim() || !form.start_date || !form.end_date) {
   toast({ title: 'Preencha nome e datas', status: 'warning' });
   return;
  }
  if (form.end_date < form.start_date) {
   toast({ title: 'Data de fim antes do início', status: 'warning' });
   return;
  }
  setSaving(true);
  try {
   if (isEdit) {
    await updateActivity(activity.id_activity, buildPayload());
   } else {
    const created = await createActivity(idSchedule, buildPayload());
    await uploadPendingFiles(created?.id_activity);
   }
   toast({ title: 'Atividade salva', status: 'success' });
   onSaved?.();
  } catch (err) {
   toast({ title: 'Erro ao salvar atividade', status: 'error' });
  } finally {
   setSaving(false);
  }
 }

 // Sobe os anexos pendentes após a criação. Falha de anexo não desfaz a
 // atividade já criada — avisa e segue.
 async function uploadPendingFiles(idActivity) {
  if (!idActivity || pendingFiles.length === 0) return;
  try {
   for (const file of pendingFiles) {
    const base64 = await fileToBase64(file);
    await addAttachment(idActivity, {
     base64,
     filename: file.name,
     mime_type: file.type || null,
     size: file.size,
    });
   }
  } catch (err) {
   toast({ title: 'Atividade criada, mas alguns anexos falharam', status: 'warning' });
  }
 }

 function pickFile() {
  fileInput.current?.click();
 }

 function removePendingFile(index) {
  setPendingFiles((prev) => prev.filter((_, i) => i !== index));
 }

 async function handleFile(e) {
  const file = e.target.files?.[0];
  e.target.value = '';
  if (!file) return;
  // Na criação não há id_activity ainda: guarda o arquivo p/ subir após salvar.
  if (!isEdit) {
   setPendingFiles((prev) => [...prev, file]);
   return;
  }
  setUploading(true);
  try {
   const base64 = await fileToBase64(file);
   const saved = await addAttachment(activity.id_activity, {
    base64,
    filename: file.name,
    mime_type: file.type || null,
    size: file.size,
   });
   setAttachments((prev) => [saved, ...prev]);
   toast({ title: 'Anexo adicionado', status: 'success' });
  } catch (err) {
   toast({ title: 'Erro ao anexar arquivo', status: 'error' });
  } finally {
   setUploading(false);
  }
 }

 async function handleRemoveAttachment(id) {
  setAttachmentToRemove(id);
 }

 async function confirmRemoveAttachment() {
  if (attachmentToRemove == null) return;
  try {
   await deleteAttachment(attachmentToRemove);
   setAttachments((prev) => prev.filter((a) => a.id_attachment !== attachmentToRemove));
  } catch (err) {
   toast({ title: 'Erro ao remover anexo', status: 'error' });
  } finally {
   setAttachmentToRemove(null);
  }
 }

 // download_url tem Content-Disposition: attachment → baixa sem abrir aba nova
 // (âncora sem target). O nome do arquivo continua abrindo inline via o Link.
 function downloadAttachment(a) {
  const href = a?.download_url || a?.url;
  if (!href) return;
  const link = document.createElement('a');
  link.href = href;
  link.setAttribute('download', a?.filename || '');
  document.body.appendChild(link);
  link.click();
  link.remove();
 }

 return (
  <>
  <Modal isOpen={isOpen} onClose={onClose} size='2xl' scrollBehavior='inside'>
   <ModalOverlay />
   <ModalContent borderRadius='20px' overflow='hidden' maxW='1100px' maxH='90vh'>
    <ModalHeader pb='8px'>
     <Box>
      <Text fontSize='20px' fontWeight='800' color='gray.800'>
       {isEdit ? 'Editar atividade' : 'Nova atividade'}
      </Text>
      <Text fontSize='sm' color='gray.500' mt='4px'>
       Organize os detalhes da atividade, prazos, responsável e fase para manter o cronograma claro.
      </Text>
     </Box>
    </ModalHeader>
    <ModalCloseButton top='16px' right='16px' />
    <ModalBody px='24px' py='8px' maxH='74vh' overflowY='auto'>
     <Stack spacing='18px'>
      <Box bg='gray.50' border='1px solid #E7E9EE' borderRadius='14px' p='16px'>
       <FormControl isRequired>
        <FormLabel fontSize='sm' fontWeight='700'>Nome da tarefa</FormLabel>
        <Input
         value={form.name}
         onChange={(e) => setField('name', e.target.value)}
         placeholder='Ex.: Revisar evidências'
        />
       </FormControl>
      </Box>

      <Box bg='gray.50' border='1px solid #E7E9EE' borderRadius='14px' p='16px'>
       <FormControl>
        <FormLabel fontSize='sm' fontWeight='700'>Descrição</FormLabel>
        <Textarea
         value={form.description}
         onChange={(e) => setField('description', e.target.value)}
         minH='96px'
         placeholder='Adicione contexto, observações ou instruções para a execução.'
        />
       </FormControl>
      </Box>

      <Box bg='gray.50' border='1px solid #E7E9EE' borderRadius='14px' p='16px'>
       <FormControl>
        <FormLabel fontSize='sm' fontWeight='700'>Fase</FormLabel>
        <Input
         value={form.fase}
         onChange={(e) => setField('fase', e.target.value)}
         list='activity-phase-options'
         placeholder='Ex.: Diagnóstico, Materialidade…'
        />
        {existingPhases.length > 0 && (
         <Box mt='10px'>
          <Text fontSize='xs' fontWeight='700' color='gray.500' mb='6px'>Fases já criadas</Text>
          <Flex gap='8px' wrap='wrap'>
           {existingPhases.map((phase) => (
            <Button
             key={phase}
             size='xs'
             variant={form.fase === phase ? 'solid' : 'outline'}
             colorScheme='blue'
             onClick={() => setField('fase', phase)}
            >
             {phase}
            </Button>
           ))}
          </Flex>
         </Box>
        )}
        <datalist id='activity-phase-options'>
         {existingPhases.map((phase) => (
          <option key={phase} value={phase} />
         ))}
        </datalist>
        <Text fontSize='sm' color='gray.500' mt='8px'>Selecione uma fase existente ou escreva uma nova.</Text>
       </FormControl>
      </Box>

      <Flex gap='12px' wrap='wrap'>
       <Box flex='1' minW='220px' bg='gray.50' border='1px solid #E7E9EE' borderRadius='14px' p='16px'>
        <FormControl isRequired>
         <FormLabel fontSize='sm' fontWeight='700'>Data de início</FormLabel>
         <Input
          type='date'
          value={form.start_date}
          onChange={(e) => setField('start_date', e.target.value)}
         />
        </FormControl>
       </Box>
       <Box flex='1' minW='220px' bg='gray.50' border='1px solid #E7E9EE' borderRadius='14px' p='16px'>
        <FormControl isRequired>
         <FormLabel fontSize='sm' fontWeight='700'>Data de fim</FormLabel>
         <Input
          type='date'
          value={form.end_date}
          onChange={(e) => setField('end_date', e.target.value)}
         />
        </FormControl>
       </Box>
      </Flex>

      <Flex gap='12px' wrap='wrap'>
       <Box flex='1' minW='220px' bg='gray.50' border='1px solid #E7E9EE' borderRadius='14px' p='16px'>
        <FormControl>
         <FormLabel fontSize='sm' fontWeight='700'>Status</FormLabel>
         <Select
          value={form.status}
          onChange={(e) => setField('status', e.target.value)}
         >
          {ACTIVITY_STATUS_OPTIONS.map((o) => (
           <option key={o.value} value={o.value}>
            {o.label}
           </option>
          ))}
         </Select>
        </FormControl>
       </Box>
       <Box
        flex='1'
        minW='220px'
        bg='linear-gradient(135deg, #F7FAFF 0%, #FFFFFF 100%)'
        border='1px solid #BBD7FF'
        borderRadius='14px'
        p='16px'
       >
        <Flex align='center' justify='space-between' gap='8px' mb='8px' flexWrap='wrap'>
         <Box>
          <Text fontSize='sm' fontWeight='700' color='gray.800'>Responsável</Text>
          <Text fontSize='xs' color='gray.500'>Clique para atribuir a pessoa responsável.</Text>
         </Box>
         {form.id_responsible ? (
          <Box bg='blue.50' color='blue.700' px='10px' py='4px' borderRadius='999px' fontSize='11px' fontWeight='700'>
           {responsibles.find((r) => String(r.id_user) === String(form.id_responsible))?.username || 'Selecionado'}
          </Box>
         ) : (
          <Box bg='orange.50' color='orange.700' px='10px' py='4px' borderRadius='999px' fontSize='11px' fontWeight='700'>
           Atribuição pendente
          </Box>
         )}
        </Flex>
        <FormControl>
         <Select
          value={form.id_responsible}
          onChange={(e) => setField('id_responsible', e.target.value)}
          placeholder='Clique para selecionar um responsável'
          size='md'
          bg='#fff'
          borderColor='#8FB8FF'
          _focus={{ borderColor: '#2D70EE', boxShadow: '0 0 0 1px #2D70EE' }}
         >
          {responsibles.map((r) => (
           <option key={r.id_user} value={r.id_user}>
            {r.username}
           </option>
          ))}
         </Select>
        </FormControl>
       </Box>
      </Flex>

      <Box bg='gray.50' border='1px solid #E7E9EE' borderRadius='14px' p='16px'>
       <FormControl>
        <FormLabel fontSize='sm' fontWeight='700'>Observações do executor</FormLabel>
        <Textarea
         value={form.executor_observations}
         onChange={(e) => setField('executor_observations', e.target.value)}
         minH='88px'
         placeholder='Adicione observações internas para o responsável.'
        />
       </FormControl>
      </Box>

      <Box bg='gray.50' border='1px solid #E7E9EE' borderRadius='14px' p='16px'>
       <Flex justify='space-between' align='center' mb='8px'>
        <FormLabel m='0' fontSize='sm' fontWeight='700'>Anexos</FormLabel>
        <Button size='xs' onClick={pickFile} isLoading={uploading}>
         Adicionar arquivo
        </Button>
        <input
         ref={fileInput}
         type='file'
         hidden
         onChange={handleFile}
        />
       </Flex>
       {!isEdit ? (
        pendingFiles.length === 0 ? (
         <Text fontSize='sm' color='gray.500'>
          Nenhum anexo selecionado. Os arquivos serão enviados ao salvar.
         </Text>
        ) : (
         <Stack spacing='8px'>
          {pendingFiles.map((file, index) => (
           <Flex
            key={`${file.name}-${index}`}
            justify='space-between'
            align='center'
            border='1px solid #E2E8F0'
            borderRadius='10px'
            px='12px'
            py='8px'
            bg='#fff'
           >
            <Box>
             <Text fontSize='sm' color='gray.700' noOfLines={1}>
              {file.name}
             </Text>
             <Text fontSize='xs' color='gray.500'>
              {formatFileSize(file.size)}
             </Text>
            </Box>
            <IconButton
             aria-label='Remover anexo'
             size='xs'
             variant='ghost'
             colorScheme='red'
             icon={<FiX />}
             onClick={() => removePendingFile(index)}
            />
           </Flex>
          ))}
         </Stack>
        )
       ) : attachments.length === 0 ? (
        <Text fontSize='sm' color='gray.500'>
         Nenhum anexo.
        </Text>
       ) : (
        <Stack spacing='8px'>
         {attachments.map((a) => (
          <Flex
           key={a.id_attachment}
           justify='space-between'
           align='center'
           border='1px solid #E2E8F0'
           borderRadius='10px'
           px='12px'
           py='8px'
           bg='#fff'
          >
           <Box>
            <Link href={a.url} isExternal color='blue.600' fontSize='sm'>
             {a.filename}
            </Link>
            <Text fontSize='xs' color='gray.500'>
             {formatDate(a.created_at)}
            </Text>
           </Box>
           <Flex align='center' gap='2px'>
            <IconButton
             aria-label='Baixar anexo'
             size='xs'
             variant='ghost'
             colorScheme='blue'
             icon={<FiDownload />}
             onClick={() => downloadAttachment(a)}
            />
            <IconButton
             aria-label='Remover anexo'
             size='xs'
             variant='ghost'
             colorScheme='red'
             icon={<FiX />}
             onClick={() => handleRemoveAttachment(a.id_attachment)}
            />
           </Flex>
          </Flex>
         ))}
        </Stack>
       )}
      </Box>
     </Stack>
    </ModalBody>
    <ModalFooter px='24px' py='16px' borderTop='1px solid #EEF1F4'>
     <Button variant='ghost' mr='12px' onClick={onClose}>
      Cancelar
     </Button>
     <Button variant='primary' onClick={handleSubmit} isLoading={saving}>
      Salvar
     </Button>
    </ModalFooter>
   </ModalContent>
  </Modal>

  <AttentionModal
   isOpen={attachmentToRemove != null}
   onClose={() => setAttachmentToRemove(null)}
   submit={confirmRemoveAttachment}
   isLoading={false}
   redType
   title='Remover anexo?'
   content='Essa ação remove o arquivo anexado à atividade. Deseja continuar?'
  />
  </>
 );
}

function fileToBase64(file) {
 return new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
 });
}

function formatFileSize(bytes) {
 if (!bytes && bytes !== 0) return '';
 if (bytes < 1024) return `${bytes} B`;
 if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
 return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
